import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import {
  fetchWhatsNewState,
  saveWhatsNewState,
  type WhatsNewCalculator,
} from '../services/whatsNewService';

/* ─── Types ──────────────────────────────────────────────────────────────────── */

export interface WhatsNewItem {
  title: string;
  /** One change, in the user's terms. Lines starting with "• " render as bullets. */
  body: string;
}

export interface WhatsNewRelease {
  /**
   * Bumped whenever these notes change. This — not the date — is what decides whether
   * an agent has already been shown the release, so editing a typo without bumping it
   * deliberately leaves everyone who has seen it alone.
   */
  version: string;
  /** Shown beside the version, e.g. "September 2026". */
  date: string;
  /** One line summing up the release, above the list. */
  headline: string;
  items: WhatsNewItem[];
}

export interface WhatsNewController {
  open: boolean;
  /** The newest release has not been acknowledged by this agent. */
  unseen: boolean;
  releases: WhatsNewRelease[];
  /** Checkbox state for "Don't show this again". */
  dontShowAgain: boolean;
  setDontShowAgain: (value: boolean) => void;
  openDialog: () => void;
  close: () => void;
}

/* ─── Controller ─────────────────────────────────────────────────────────────── */

/**
 * Announces the newest release once per agent, then stays out of the way.
 *
 * `releases` is newest-first; only `releases[0].version` decides whether to open on
 * arrival, so adding an older entry to the history never re-interrupts anyone.
 *
 * The acknowledgement lives on the server, keyed by CEA — the app runs inside a native
 * WebView whose local storage is cleared between launches, so a local flag would
 * re-announce the same release every time. Server-side also means an agent who
 * dismissed the notes on their phone does not meet them again on their laptop.
 *
 * Nothing opens until that state has loaded, and a failed read opens nothing at all: a
 * dialog over someone mid-case is a worse outcome than a missed announcement. The
 * megaphone button opens it on demand either way.
 *
 * `untilSuppressed` makes it nag instead: it opens on every visit until the agent ticks
 * "Don't show this again", whether or not they have already closed this version.
 */
export function useWhatsNew(
  calculator: WhatsNewCalculator,
  releases: WhatsNewRelease[],
  { untilSuppressed = false }: { untilSuppressed?: boolean } = {},
): WhatsNewController {
  const latest = releases[0]?.version ?? '';
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // Guards the auto-open against a second run (React 18 StrictMode mounts twice in
  // development), which would otherwise reopen the dialog the agent just closed.
  const announced = useRef(false);

  useEffect(() => {
    if (!latest) return;
    let mounted = true;

    void (async () => {
      const state = await fetchWhatsNewState();
      // null = unreadable. Treat it as "already seen" rather than announce on every
      // page load while the backend is unreachable.
      if (!mounted || !state) return;
      const seen = state[calculator];
      // Start the checkbox from the stored opt-out, so reopening the notes from the
      // megaphone and closing them doesn't quietly opt the agent back in.
      if (seen?.suppressed) {
        setDontShowAgain(true);
        return;
      }
      if (!untilSuppressed && seen?.seenVersion === latest) return;
      setUnseen(true);
      if (!announced.current) {
        announced.current = true;
        setOpen(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [calculator, latest, untilSuppressed]);

  const close = useCallback(() => {
    setOpen(false);
    setUnseen(false);
    if (!latest) return;
    // Fire-and-forget: the dialog closes immediately either way, and a failed write
    // costs the agent one more announcement next visit, nothing more.
    void saveWhatsNewState(calculator, { seenVersion: latest, suppressed: dontShowAgain });
  }, [calculator, dontShowAgain, latest]);

  const openDialog = useCallback(() => setOpen(true), []);

  return { open, unseen, releases, dontShowAgain, setDontShowAgain, openDialog, close };
}

/* ─── Launch button ──────────────────────────────────────────────────────────── */

/** Megaphone button that reopens the notes; dotted while the newest is unacknowledged. */
export const WhatsNewLaunchButton: React.FC<{
  controller: WhatsNewController;
  /** Calculator name used in the tooltip, e.g. "the Buying Calculator". */
  label: string;
  size?: 'small' | 'medium';
}> = ({ controller, label, size = 'small' }) => (
  <Tooltip title={`What's New in ${label}`}>
    <Badge
      color="error"
      variant="dot"
      invisible={!controller.unseen}
      overlap="circular"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <IconButton
        onClick={controller.openDialog}
        size={size}
        sx={{
          border: '2px solid',
          borderColor: 'currentColor',
          color: '#D97706',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <CampaignOutlinedIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
    </Badge>
  </Tooltip>
);

/* ─── Dialog ─────────────────────────────────────────────────────────────────── */

/** Renders one item's body, turning "• " lines into hanging bullets. */
const ItemBody: React.FC<{ body: string }> = ({ body }) => (
  <>
    {body.split('\n').map((line, i) => {
      const bullet = line.startsWith('• ');
      return (
        <Typography
          key={i}
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.85rem',
            lineHeight: 1.6,
            pl: bullet ? 1.5 : 0,
            textIndent: bullet ? '-0.9rem' : 0,
            mt: i === 0 ? 0 : 0.25,
          }}
        >
          {line}
        </Typography>
      );
    })}
  </>
);

const WhatsNewDialog: React.FC<{ controller: WhatsNewController }> = ({ controller }) => {
  const { open, close, releases, dontShowAgain, setDontShowAgain } = controller;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CampaignOutlinedIcon sx={{ color: '#D97706' }} />
        What&apos;s New
      </DialogTitle>

      <DialogContent dividers>
        {releases.map((release, releaseIdx) => (
          <Box key={release.version} sx={{ mb: releaseIdx === releases.length - 1 ? 0 : 3 }}>
            {releaseIdx > 0 && <Divider sx={{ mb: 2.5 }} />}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={release.version}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                  bgcolor: 'rgba(217,119,6,0.12)',
                  color: '#B45309',
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {release.date}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, fontSize: '0.9rem' }}>
              {release.headline}
            </Typography>

            {release.items.map((item, itemIdx) => (
              <Box
                key={item.title}
                sx={{
                  mb: itemIdx === release.items.length - 1 ? 0 : 2,
                  pl: 1.5,
                  borderLeft: '3px solid rgba(217,119,6,0.35)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 0.5 }}>
                  {item.title}
                </Typography>
                <ItemBody body={item.body} />
              </Box>
            ))}
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <FormControlLabel
          sx={{ flex: 1, mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                Don&apos;t show this again
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Still available from the megaphone beside the title
              </Typography>
            </Box>
          }
        />
        <Button onClick={close} variant="contained" sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#B45309' } }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewDialog;
